import Link from 'next/link';
import Image from 'next/image';
import { PressReleaseBlock, PressReleaseData } from '@/types/pressRelease.types';
import styles from './pressReleaseComponent.module.scss';

const renderBlock = (block: PressReleaseBlock, index: number) => {
  switch (block.type) {
    case 'paragraph':
      return (
        <div key={index} className={styles.pressReleaseBlock}>
          <p className={styles.pressReleaseText}>
            {block.segments.map((segment, i) =>
              segment.href ? (
                <Link key={i} href={segment.href} target="_blank" className={styles.pressReleaseText}>
                  {segment.text}
                </Link>
              ) : (
                <span key={i}>{segment.text}</span>
              )
            )}
          </p>
        </div>
      );

    case 'quote':
      return (
        <div key={index} className={styles.pressReleaseBlock}>
          <blockquote className={styles.pressReleaseQuote}>{block.text}</blockquote>
        </div>
      );

    case 'sectionTitle':
      return (
        <div key={index} className={styles.pressReleaseBlock}>
          <h4 className={styles.pressReleaseSectionTitle}>{block.text}</h4>
        </div>
      );

    case 'list':
      return (
        <div key={index} className={styles.pressReleaseBlock}>
          <ul className={styles.pressReleaseList}>
            {block.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      );

    case 'pricing':
      return (
        <div key={index} className={styles.pressReleasePricingItem}>
          <div className={styles.pressReleasePricingName}>{block.name}</div>
          <div className={styles.pressReleasePricingPrice}>{block.price}</div>
          <div className={styles.pressReleasePricingBreakdown}>{block.breakdown}</div>
        </div>
      );

    case 'image':
      return (
        <div key={index} className={styles.pressReleaseImageBlock}>
          <Image
            src={block.src}
            alt={block.alt}
            width={1200}
            height={800}
            className={styles.pressReleaseImage}
          />
        </div>
      );

    default:
      return null;
  }
};

const groupPricingBlocks = (blocks: PressReleaseBlock[]) => {
  const result: (PressReleaseBlock | { type: 'pricingGroup'; items: Extract<PressReleaseBlock, { type: 'pricing' }>[] })[] = [];

  let i = 0;
  while (i < blocks.length) {
    if (blocks[i].type === 'pricing') {
      const group: Extract<PressReleaseBlock, { type: 'pricing' }>[] = [];
      while (i < blocks.length && blocks[i].type === 'pricing') {
        group.push(blocks[i] as Extract<PressReleaseBlock, { type: 'pricing' }>);
        i++;
      }
      result.push({ type: 'pricingGroup', items: group });
    } else {
      result.push(blocks[i]);
      i++;
    }
  }

  return result;
};

type Props = {
  data: PressReleaseData;
};

const PressReleaseComponent = ({ data }: Props) => {
  const groupedBlocks = groupPricingBlocks(data.blocks);

  return (
    <section className="container">
      <div className={styles.pressReleaseContainer}>
        <div className={styles.pressReleaseBlock}>
          <p className={styles.pressReleaseLabel}>{data.releaseLabel}</p>
          <h1 className={styles.pressReleaseTitle}>{data.title}</h1>
        </div>

        {groupedBlocks.map((block, index) => {
          if (block.type === 'pricingGroup') {
            return (
              <div key={index} className={`${styles.pressReleaseBlock} ${styles.pressReleasePricingGroup}`}>
                {block.items.map((item, i) => renderBlock(item, i))}
              </div>
            );
          }
          return renderBlock(block as PressReleaseBlock, index);
        })}
      </div>
    </section>
  );
};

export default PressReleaseComponent;
